import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Pencil, Plus, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  getAdminMerchantsGetAdminClientsClientIdGuidMerchantsQueryKey,
  useAdminMerchantsGetAdminClientsClientIdGuidMerchants,
  useAdminMerchantsPostAdminMerchantsIdGuidActivate,
  useAdminMerchantsPostAdminMerchantsIdGuidSuspend,
} from '@/lib/api/admin-merchants/admin-merchants';
import { useAdminClientsGetAdminClientsIdGuid } from '@/lib/api/admin-clients/admin-clients';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useHasRole } from '@/lib/auth/useAuth';
import { AdminCreateMerchantDialog } from '@/pages/Merchants/AdminCreateMerchantDialog';
import { ClientSecretsCard } from './ClientSecretsCard';
import { EditClientDialog } from './EditClientDialog';
import { PortalUsersAdminDialog } from './PortalUsersAdminDialog';
import { WebhookEndpointsCard } from './WebhookEndpointsCard';

/**
 * Admin hierarchical drill-down: /clients/:id shows one client's profile
 * plus its merchant list with inline "Add merchant" + drill-down into each
 * merchant for its stores. Mirrors the per-partner portal view but from the
 * admin perspective (can see any client).
 */
export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canMutate = useHasRole('Admin');
  const queryClient = useQueryClient();

  const [editClientOpen, setEditClientOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [createMerchantOpen, setCreateMerchantOpen] = useState(false);

  const clientQuery = useAdminClientsGetAdminClientsIdGuid(id ?? '', { query: { enabled: !!id } });
  const merchantsQuery = useAdminMerchantsGetAdminClientsClientIdGuidMerchants(id ?? '', {
    query: { enabled: !!id },
  });

  const suspend = useAdminMerchantsPostAdminMerchantsIdGuidSuspend();
  const activate = useAdminMerchantsPostAdminMerchantsIdGuidActivate();

  const toggleMerchant = async (merchantId: string, isActive: boolean) => {
    try {
      if (isActive) await suspend.mutateAsync({ id: merchantId });
      else await activate.mutateAsync({ id: merchantId });
      await queryClient.invalidateQueries({
        queryKey: getAdminMerchantsGetAdminClientsClientIdGuidMerchantsQueryKey(id ?? ''),
      });
    } catch {
      toast.error('Could not change merchant status.');
    }
  };

  if (!id) return <div className="text-sm text-destructive">Missing client id.</div>;

  if (clientQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading client…</div>;
  }
  if (!clientQuery.data) {
    return (
      <div className="text-sm text-destructive">
        Client not found.{' '}
        <Link to="/clients" className="font-medium text-primary hover:underline">
          Back to list
        </Link>
      </div>
    );
  }

  const client = clientQuery.data;

  return (
    <section className="space-y-5">
      <Button variant="ghost" size="sm" className="gap-2" onClick={() => navigate('/clients')}>
        <ArrowLeft className="h-4 w-4" />
        All clients
      </Button>

      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{client.name}</h1>
          <p className="font-mono text-xs text-muted-foreground">{client.clientId}</p>
        </div>
        {canMutate && (
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setUsersOpen(true)}>
              <UserPlus className="h-4 w-4" /> Portal users
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => setEditClientOpen(true)}>
              <Pencil className="h-4 w-4" /> Edit client
            </Button>
          </div>
        )}
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Status</CardDescription>
            <CardTitle>
              {client.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Rate limit / min</CardDescription>
            <CardTitle className="text-3xl tabular-nums">{client.rateLimitPerMin}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Allowed IPs</CardDescription>
            <CardTitle className="text-sm font-normal">
              {client.allowedIpCidrs && client.allowedIpCidrs.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {client.allowedIpCidrs.map((cidr) => (
                    <Badge key={cidr} variant="secondary">{cidr}</Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">Any</span>
              )}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>Merchants</CardTitle>
            <CardDescription>
              Adyen merchant accounts under this client. Click a row to manage its stores.
            </CardDescription>
          </div>
          {canMutate && (
            <Button size="sm" className="gap-2" onClick={() => setCreateMerchantOpen(true)}>
              <Plus className="h-4 w-4" /> Add merchant
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {merchantsQuery.isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-muted" />
              ))}
            </div>
          ) : !merchantsQuery.data || merchantsQuery.data.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No merchants yet.{canMutate && ' Add one to enable per-merchant routing.'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Merchant ID</th>
                  <th className="p-3 font-medium">Stores</th>
                  <th className="p-3 font-medium">Status</th>
                  {canMutate && <th className="w-36 p-3" />}
                  <th className="w-8 p-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {merchantsQuery.data.map((m) => (
                  <tr
                    key={m.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => navigate(`/merchants/${m.id}`)}
                  >
                    <td className="p-3 font-medium">{m.name}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{m.merchantId}</td>
                    <td className="p-3 tabular-nums">{m.storeCount}</td>
                    <td className="p-3">
                      {m.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Suspended</Badge>}
                    </td>
                    {canMutate && (
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleMerchant(m.id, m.isActive)}
                          disabled={suspend.isPending || activate.isPending}
                        >
                          {m.isActive ? 'Suspend' : 'Activate'}
                        </Button>
                      </td>
                    )}
                    <td className="p-3 text-muted-foreground">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ClientSecretsCard clientId={id} />

      <WebhookEndpointsCard clientId={id} />

      {canMutate && (
        <EditClientDialog clientId={editClientOpen ? id : null} onOpenChange={(open) => !open && setEditClientOpen(false)} />
      )}
      {canMutate && (
        <PortalUsersAdminDialog
          clientId={usersOpen ? id : null}
          clientName={client.name}
          onOpenChange={(open) => !open && setUsersOpen(false)}
        />
      )}
      {canMutate && (
        <AdminCreateMerchantDialog
          clientId={createMerchantOpen ? id : null}
          onOpenChange={(open) => !open && setCreateMerchantOpen(false)}
        />
      )}
    </section>
  );
}
